/**
 * API Error Handler
 * Centralized error handling for AWS Amplify API calls
 */
import * as Sentry from '@sentry/react';
import i18n from 'i18next';

// Billing refuses writes into a paused organisation with
// 403 { code: 'ORG_READ_ONLY', scope: 'organisation' | 'provider', canPay }.
// Fired on every such refusal so the billing banner can refresh its standing.
export const ORG_READ_ONLY_EVENT = 'org-read-only';

const orgReadOnlyMessage = ({ scope, canPay }) => {
  if (scope === 'provider') {
    return i18n.t("Jou eie organisasie se Root-rekening is gepouseer, so jy kan nie in kliënte se rekeninge werk nie.");
  }
  return canPay
    ? i18n.t("Root is gepouseer omdat jou organisasie se rekening nie betaal is nie. Betaal om voort te gaan. EFT-betalings kan tot 'n werksdag neem om te wys.")
    : i18n.t("Root is gepouseer vir jou organisasie. Diens gepouseer, kontak jou kantoor.");
};

/**
 * Extracts a clean error object from any error type. An ORG_READ_ONLY refusal
 * gets the plain "paused" message so every call site shows it unchanged.
 * @param {Error|Object} error - The error from API call
 * @returns {Object} Clean error object with code, message, type
 */
export const parseApiError = (error) => {
  const cleanError = parseRawApiError(error);
  if (cleanError === error || cleanError.data?.code !== 'ORG_READ_ONLY') return cleanError;

  const message = orgReadOnlyMessage(cleanError.data);
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(ORG_READ_ONLY_EVENT));
  return { ...cleanError, code: 403, type: 'api', message, data: { ...cleanError.data, message } };
};

const parseRawApiError = (error) => {
  // Already a normalized error object
  if (
    typeof error?.code === 'number' &&
    typeof error?.message === 'string' &&
    typeof error?.type === 'string'
  ) {
    return error;
  }

  // Amplify API error with response
  if (error?.response) {
    return {
      code: error.response.status ?? 500,
      message: error.response.data?.message || error.response.statusText || 'API Error',
      type: 'api',
      data: error.response.data
    };
  }

  // Direct response-like object used by some legacy call sites
  if (
    typeof error?.status === 'number' ||
    typeof error?.statusText === 'string' ||
    error?.data !== undefined
  ) {
    return {
      code: error.status ?? 500,
      message: error.data?.message || error.statusText || 'API Error',
      type: 'api',
      data: error.data
    };
  }

  // Network error (no response)
  if (error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK') {
    return {
      code: 0,
      message: 'Network error - check your connection',
      type: 'network'
    };
  }

  // Timeout
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return {
      code: 0,
      message: 'Request timed out',
      type: 'timeout'
    };
  }

  // String errors (sometimes error objects get stringified)
  if (typeof error === 'string') {
    const trimmed = error.trim();

    // If it looks like JSON, try to parse for a nicer message
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        const message = parsed?.message || parsed?.errorMessage || trimmed;

        const parsedStatus = parsed?.status ?? parsed?.statusCode ?? parsed?.code;
        const hasNumericStatus = typeof parsedStatus === 'number';

        return {
          code: hasNumericStatus ? parsedStatus : 0,
          message,
          type: hasNumericStatus ? 'api' : 'unknown',
          data: parsed
        };
      } catch {
        // ignore
      }
    }

    return {
      code: 0,
      message: trimmed,
      type: 'unknown'
    };
  }

  // Generic error
  return {
    code: 0,
    message: error?.message || 'An unexpected error occurred',
    type: 'unknown'
  };
};


// --- Sentry capture throttle (per session) ---------------------------------
// Suppress repeat Sentry captures for the same (type, url) within a short
// window. Keeps noisy network/timeout loops from flooding the project.
const _captureSeen = new Map();
const _CAPTURE_TTL_MS = 60_000;
const shouldCaptureToSentry = (type, url) => {
  const key = `${type}:${url ?? 'unknown'}`;
  const now = Date.now();
  const last = _captureSeen.get(key);
  if (last && now - last < _CAPTURE_TTL_MS) return false;
  _captureSeen.set(key, now);
  return true;
};

// Build a sanitized Error mirroring the original error's shape but with no
// request config attached. Defense in depth against AxiosError -> Sentry leaks.
// Exported so call sites doing their own Sentry.captureException (e.g. capturing
// a wider error range than handleApiError's 5xx threshold) can sanitize first
// instead of passing the raw AxiosError, which carries the Cognito Bearer JWT
// in config.headers.Authorization.
export const buildSanitizedError = (error, cleanError) => {
  if (error instanceof Error) {
    const sanitized = new Error(error.message);
    sanitized.name = error.name;
    if (cleanError?.type) sanitized.type = cleanError.type;
    if (cleanError?.code !== undefined) sanitized.code = cleanError.code;
    return sanitized;
  }
  // Non-Error inputs: return a fresh Error carrying just the message + type.
  const msg = typeof error === 'string' ? error : (cleanError?.message || 'API Error');
  const sanitized = new Error(msg);
  if (cleanError?.type) sanitized.type = cleanError.type;
  if (cleanError?.code !== undefined) sanitized.code = cleanError.code;
  return sanitized;
};

/**
 * Maps the central API error shape to legacy response-like consumers.
 *
 * NOTE on status 408: 'timeout' here refers to a client-side timeout
 * (Axios ECONNABORTED / no server response). HTTP 408 strictly means the
 * *server* sent a Request Timeout response. We re-use 408 as a CE-internal
 * convention to signal "request timed out before completion" to consumers
 * (Alert.jsx has a localised 408 entry). This is a documented mis-use; do
 * not interpret status 408 from this helper as a server-side response.
 *
 * @param {*} error - Error caught from an API request
 * @param {number} defaultStatus - Status code to use for non-HTTP errors
 * @returns {Object} Response-like error object with a numeric status property
 */
export const getApiErrorResponse = (error, defaultStatus = 500) => {
  const cleanError = parseApiError(error);
  let status;
  if (cleanError.type === 'network') {
    status = 0;
  } else if (cleanError.type === 'timeout') {
    status = 408;
  } else if (cleanError.type === 'api' && cleanError.code > 0) {
    status = cleanError.code;
  } else {
    status = defaultStatus;
  }

  if (cleanError.type === 'network' || cleanError.type === 'timeout') {
    try {
      const sanitizedUrl = typeof error?.config?.url === 'string'
        ? error.config.url.split('?')[0]
        : 'unknown';
      if (shouldCaptureToSentry(cleanError.type, sanitizedUrl)) {
        const sanitized = buildSanitizedError(error, cleanError);
        Sentry.withScope((scope) => {
          scope.setLevel('warning');
          scope.setTag('error_type', cleanError.type);
          scope.setContext('request', {
            url: sanitizedUrl,
            method: error?.config?.method ?? 'unknown',
          });
          // Group all transient connectivity captures by error type, NOT by the
          // catching component's stack frame. A single network blip on a page
          // with concurrent requests otherwise fingerprints into one Sentry
          // issue per call site (e.g. Dashboard.jsx vs Tydregister.jsx), even
          // though it is the same event. Collapse them into one low-priority
          // issue per type so the dashboard reflects "a connectivity blip"
          // rather than N duplicates.
          scope.setFingerprint(['api-connectivity-error', cleanError.type]);
          Sentry.captureException(sanitized);
        });
      }
    } catch (_sentryErr) {
      // swallow — Sentry must never crash error handlers
    }
  }

  // For network/timeout, omit `data` so AlertBox falls through to its localised
  // (Afrikaans) message for code 0 / 408 instead of overriding it with the
  // English placeholder from parseApiError.
  const isNetworkOrTimeout = cleanError.type === 'network' || cleanError.type === 'timeout';
  const data = isNetworkOrTimeout
    ? undefined
    : (cleanError.data || { message: cleanError.message || 'Onbekende fout' });

  return {
    status,
    data,
    statusText: cleanError.message,
    type: cleanError.type,
  };
};

/**
 * Captures an exception to Sentry from an out-of-band call site (i.e. one that
 * does its own capture rather than routing through handleApiError /
 * getApiErrorResponse). Bundles the three controls those helpers apply so call
 * sites get them for free: (a) buildSanitizedError strips the AxiosError request
 * config (Cognito Bearer JWT, request body) before capture, (b)
 * shouldCaptureToSentry rate-limits repeat captures for the same error type
 * within the TTL window, and (c) a try/catch swallows any Sentry SDK throw so a
 * broken transport can never surface an unhandled rejection.
 * @param {Error|Object} error - The raw error caught from an API call
 * @param {Object} cleanError - The normalized envelope from parseApiError
 * @param {function} [configureScope] - Optional callback to set tags/context on
 *   the Sentry scope before capture
 */
export const captureSanitizedException = (error, cleanError, configureScope) => {
  if (!shouldCaptureToSentry(cleanError?.type, error?.config?.url)) return;
  try {
    Sentry.withScope((scope) => {
      if (typeof configureScope === 'function') configureScope(scope);
      Sentry.captureException(buildSanitizedError(error, cleanError));
    });
  } catch (_sentryErr) {
    // swallow — Sentry must never crash error handlers
  }
};

/**
 * Handles API errors with optional Sentry logging
 * @param {Error|Object} error - The error from API call
 * @param {Object} options - Options for error handling
 * @param {boolean} options.logToSentry - Whether to log to Sentry (default: true for 5xx)
 * @param {string} options.context - Additional context for the error
 * @returns {Object} Clean error object
 */
export const handleApiError = (error, options = {}) => {
  const { logToSentry = true, context = '' } = options;
  const cleanError = parseApiError(error);

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error(`[API Error${context ? ` - ${context}` : ''}]`, cleanError);
  }

  // Only log server errors (5xx) to Sentry, not network/client errors
  if (logToSentry && cleanError.type === 'api' && cleanError.code >= 500) {
    try {
      const sanitizedUrl = typeof error?.config?.url === 'string'
        ? error.config.url.split('?')[0]
        : 'unknown';
      const sanitized = buildSanitizedError(error, cleanError);
      Sentry.withScope((scope) => {
        scope.setTag('error_type', cleanError.type);
        scope.setTag('error_code', String(cleanError.code));
        scope.setContext('request', {
          url: sanitizedUrl,
          method: error?.config?.method ?? 'unknown',
        });
        if (context) scope.setContext('api_context', { context });
        Sentry.captureException(sanitized);
      });
    } catch (_sentryErr) {
      // swallow — Sentry must never crash error handlers
    }
  }

  return cleanError;
};

/**
 * Wrapper for API calls with automatic error handling
 * @param {Promise} apiCall - The API call promise
 * @param {Object} options - Options passed to handleApiError
 * @returns {Promise} Resolves with the API response (or rejects with clean error)
 */
export const safeApiCall = async (apiCall, options = {}) => {
  try {
    const response = await apiCall;
    return response;
  } catch (error) {
    throw handleApiError(error, options);
  }
};

export default {
  parseApiError,
  buildSanitizedError,
  captureSanitizedException,
  getApiErrorResponse,
  handleApiError,
  safeApiCall
};
